import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Gift, Sparkles, CreditCard, Mail, Heart, Award } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GiftCardPurchase = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const [settings, setSettings] = useState(null);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [purchaserName, setPurchaserName] = useState('');
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [pointsToUse, setPointsToUse] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  useEffect(() => {
    (async () => {
      try {
        const s = await axios.get(`${API_URL}/api/gift-cards/settings`);
        setSettings(s.data);
        if (s.data?.preset_amounts?.length) setAmount(s.data.preset_amounts[1] || s.data.preset_amounts[0]);
      } catch (e) {
        toast.error('Failed to load gift card settings');
      }
      if (user) {
        setPurchaserName(user.name || '');
        setPurchaserEmail(user.email || '');
        try {
          const r = await axios.get(`${API_URL}/api/customer/loyalty`, { withCredentials: true });
          setPointsBalance(r.data?.balance || 0);
        } catch (_) { /* not a customer */ }
      }
      setLoading(false);
    })();
  }, [user]);

  const finalAmount = customAmount ? Number(customAmount) || 0 : amount;
  const rate = settings?.points_per_dollar || 100;
  const maxPointsUsable = Math.min(pointsBalance, finalAmount * rate);
  const pointsValue = pointsToUse / rate;
  const cashRequired = Math.max(0, finalAmount - pointsValue);

  const submit = async () => {
    if (!recipientName.trim()) return toast.error('Recipient name is required');
    if (!recipientEmail.trim() || !recipientEmail.includes('@')) return toast.error('Valid recipient email is required');
    if (!purchaserEmail.trim() || !purchaserEmail.includes('@')) return toast.error('Your email is required (so we can send a confirmation)');
    if (finalAmount < (settings?.min_amount || 10)) return toast.error(`Minimum amount is $${settings?.min_amount || 10}`);
    if (finalAmount > (settings?.max_amount || 500)) return toast.error(`Maximum amount is $${settings?.max_amount || 500}`);

    setSubmitting(true);
    try {
      const payload = {
        amount: finalAmount,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim().toLowerCase(),
        message: message.trim(),
        purchaser_name: purchaserName.trim(),
        purchaser_email: purchaserEmail.trim().toLowerCase(),
        points_to_use: pointsToUse,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API_URL}/api/gift-cards/purchase`, payload, {
        withCredentials: !!user,
      });
      if (res.data.requires_payment === false) {
        toast.success(`Gift card ${res.data.code} issued! It has been emailed to ${recipientName}.`);
        navigate(`/gift-cards/success?code=${res.data.code}`);
        return;
      }
      if (paymentMethod === 'stripe' && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (paymentMethod === 'paypal' && res.data.paypal?.order_id) {
        // For simplicity: redirect to the PayPal approve URL constructed from the order.
        // PayPal returns the order in res.data — for the MVP, redirect via the
        // approval link contained in the order's `links` field would be ideal,
        // but our backend doesn't surface that. So we direct the user back to a
        // PayPal confirm page. (Future enhancement: capture links from backend.)
        toast.message('PayPal order created. Use the confirm link in your email or contact us to finalize.');
        navigate(`/gift-cards/success?pending=1&gift_card_id=${res.data.gift_card_id}&order_id=${res.data.paypal.order_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div><Header /><div className="container mx-auto px-4 py-16">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-4xl" data-testid="gift-card-purchase-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF3B6B]/10 text-[#FF3B6B] text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> THE GIFT OF A PARTY
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Send a Creative Birthdays gift card
          </h1>
          <p className="text-[#6B6B7A] mt-3 max-w-2xl mx-auto">
            Choose an amount, write a heartfelt note, and we'll email a beautiful PDF voucher straight to the lucky recipient.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* LEFT — FORM */}
          <div className="lg:col-span-3 space-y-6">
            {/* Amount selector */}
            <div className="bg-white border border-[#E8E5DF] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">Amount</h3></div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {(settings?.preset_amounts || [50, 100, 250]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { setAmount(p); setCustomAmount(''); }}
                    data-testid={`gc-preset-${p}`}
                    className={`py-3 rounded-xl border-2 font-bold text-lg transition-all ${
                      !customAmount && amount === p
                        ? 'border-[#FF3B6B] bg-[#FF3B6B]/5 text-[#FF3B6B]'
                        : 'border-[#E8E5DF] text-[#1E1E24] hover:border-[#FF3B6B]/50'
                    }`}
                  >
                    ${p}
                  </button>
                ))}
              </div>
              <Label className="text-xs text-[#6B6B7A]">Or enter a custom amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A] font-semibold">$</span>
                <Input
                  type="number"
                  min={settings?.min_amount || 10}
                  max={settings?.max_amount || 500}
                  step="1"
                  inputMode="numeric"
                  placeholder={`Min $${settings?.min_amount || 10} · Max $${settings?.max_amount || 500}`}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="pl-7"
                  data-testid="gc-custom-amount"
                />
              </div>
            </div>

            {/* Recipient */}
            <div className="bg-white border border-[#E8E5DF] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3"><Heart className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">Recipient</h3></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Their name</Label>
                  <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Jamie's Mom" data-testid="gc-recipient-name" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Their email</Label>
                  <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="jamie@example.com" data-testid="gc-recipient-email" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs text-[#6B6B7A]">Personal message <span className="text-[#6B6B7A]/70">(optional)</span></Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                  placeholder="Wishing you the most magical party ever! Use this to book your special day."
                  rows={3}
                  data-testid="gc-message"
                />
                <p className="text-[10px] text-[#6B6B7A] mt-1">{message.length}/500</p>
              </div>
            </div>

            {/* Purchaser */}
            <div className="bg-white border border-[#E8E5DF] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">From</h3></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Your name</Label>
                  <Input value={purchaserName} onChange={(e) => setPurchaserName(e.target.value)} placeholder="Your name" data-testid="gc-purchaser-name" disabled={!!user} />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Your email</Label>
                  <Input value={purchaserEmail} onChange={(e) => setPurchaserEmail(e.target.value)} placeholder="you@example.com" data-testid="gc-purchaser-email" disabled={!!user} />
                </div>
              </div>
              {!user && (
                <p className="text-xs text-[#6B6B7A] mt-2">
                  Want to use loyalty points? <a href="/login" className="text-[#FF3B6B] underline">Log in</a> first.
                </p>
              )}
            </div>

            {/* Loyalty points */}
            {user && pointsBalance > 0 && (
              <div className="bg-gradient-to-br from-[#FF3B6B]/5 to-[#FB923C]/5 border-2 border-dashed border-[#FF3B6B]/30 rounded-2xl p-5" data-testid="gc-loyalty-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">Use loyalty points</h3></div>
                  <span className="text-xs text-[#6B6B7A]">Balance: <b className="text-[#FF3B6B]">{pointsBalance.toLocaleString()} pts</b></span>
                </div>
                <p className="text-xs text-[#6B6B7A] mb-3">{rate} pts = $1. You can apply up to <b>{maxPointsUsable.toLocaleString()} pts</b> (${(maxPointsUsable / rate).toFixed(2)}) toward this purchase.</p>
                <input
                  type="range" min="0" max={maxPointsUsable} step={rate}
                  value={pointsToUse}
                  onChange={(e) => setPointsToUse(Math.min(maxPointsUsable, Math.max(0, Number(e.target.value))))}
                  className="w-full accent-[#FF3B6B]"
                  data-testid="gc-points-slider"
                />
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="text-[#6B6B7A]">Using <b className="text-[#FF3B6B]" data-testid="gc-points-using">{pointsToUse.toLocaleString()} pts</b></span>
                  <span className="text-[#6B6B7A]">= <b>${pointsValue.toFixed(2)}</b> off</span>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — SUMMARY */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 sticky top-24" data-testid="gc-summary">
              <h3 className="font-semibold text-[#1E1E24] mb-4">Order Summary</h3>
              <div className="space-y-3 mb-5">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Gift card value</span>
                  <span className="font-semibold text-[#1E1E24]" data-testid="gc-summary-amount">${finalAmount.toFixed(2)}</span>
                </div>
                {pointsToUse > 0 && (
                  <div className="flex justify-between text-sm text-[#FF3B6B]">
                    <span>Loyalty points</span>
                    <span data-testid="gc-summary-points">-${pointsValue.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-[#E8E5DF] pt-3 flex justify-between text-base">
                  <span className="font-semibold text-[#1E1E24]">You pay</span>
                  <span className="font-bold text-[#FF3B6B] text-xl" data-testid="gc-summary-pay">${cashRequired.toFixed(2)}</span>
                </div>
              </div>

              {cashRequired > 0 && (
                <div className="space-y-2 mb-4">
                  <Label className="text-xs text-[#6B6B7A]">Payment method</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['stripe', 'paypal'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPaymentMethod(m)}
                        data-testid={`gc-pay-${m}`}
                        className={`py-2 rounded-xl border-2 text-xs font-semibold uppercase transition-all ${
                          paymentMethod === m
                            ? 'border-[#FF3B6B] bg-[#FF3B6B]/5 text-[#FF3B6B]'
                            : 'border-[#E8E5DF] text-[#6B6B7A]'
                        }`}
                      >
                        {m === 'stripe' ? 'Card' : 'PayPal'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={submit}
                disabled={submitting}
                className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white py-6 rounded-full font-semibold text-base"
                data-testid="gc-submit-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {submitting ? 'Processing…' : cashRequired > 0 ? `Pay $${cashRequired.toFixed(2)}` : 'Issue gift card'}
              </Button>

              <p className="text-[11px] text-[#6B6B7A] text-center mt-3">
                Voucher emailed as a PDF the moment payment clears. Valid for {(settings?.expiry_days || 365)} days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardPurchase;
